/**
 * THE WIRE - Data Loading Service
 * Fetches analysis data from static JSON files in /intel/
 */

import {
  PITCH_TYPE_LABELS,
  TIGERS_PITCHERS,
  COLLEGE_PITCHERS,
  INTEL_SOURCES,
  normalizePitcherName,
  getPitcherInfo,
} from './wireTypes';

// =============================================================================
// CONFIGURATION
// =============================================================================

const INTEL_BASE_PATH = process.env.REACT_APP_INTEL_BASE_URL || '/intel';
const DEFAULT_GCS_BUCKET = 'slam-pitch-video';

// Current intel source - can be changed dynamically
let currentIntelSource = 'tigers';

/**
 * Set the current intel source
 * @param {string} sourceId - 'tigers' | 'wake-forest'
 */
export function setIntelSource(sourceId) {
  if (INTEL_SOURCES[sourceId]) {
    currentIntelSource = sourceId;
    console.log(`Intel source set to: ${INTEL_SOURCES[sourceId].name}`);
  } else {
    console.warn(`Unknown intel source: ${sourceId}`);
  }
}

/**
 * Get the current intel source
 */
export function getIntelSource() {
  return INTEL_SOURCES[currentIntelSource] || INTEL_SOURCES.tigers;
}

/**
 * Get available intel sources
 */
export function getAvailableIntelSources() {
  return Object.values(INTEL_SOURCES);
}

// =============================================================================
// SET POSITION MAPPING
// =============================================================================

/**
 * Map situation field to human-readable set position
 * The situation comes from the video filename: {pitcher}_{date}_{pitch_type}_{situation}_{uid}.mp4
 *
 * Situations:
 *   - bases_empty → Windup (no runners on base)
 *   - runner_on_1st / runner_on_first → Stretch (runners on base)
 */
export function getSetPositionFromSituation(situation) {
  if (!situation) return 'Unknown';

  const sitLower = situation.toLowerCase();

  // Windup - no runners on base
  if (sitLower === 'bases_empty' || sitLower === 'bases-empty' || sitLower === 'windup') {
    return 'Windup';
  }

  // Stretch - runners on base
  if (sitLower.includes('runner') || sitLower === 'stretch' || sitLower.includes('risp')) {
    return 'Stretch';
  }

  // Default to Windup if unknown (most common)
  return 'Windup';
}

/**
 * Parse situation from filename if not provided in data
 * Filename format: {pitcher}_{date}_{pitch_type}_{situation}_{uid}.mp4
 */
export function parseSituationFromFilename(filename) {
  if (!filename) return null;

  // Extract situation from filename pattern
  // e.g., Skubal_2025-04-08_CB_bases_empty_72e3e7c8.mp4
  const parts = filename.replace('.mp4', '').split('_');

  // Situation is the 4th part (index 3), but could span multiple parts
  // Handle both "bases_empty" and "runner_on_1st" patterns
  if (parts.length >= 5) {
    // Check for multi-word situations like "runner_on_1st"
    if (parts[3] === 'runner' && parts.length >= 6) {
      return `${parts[3]}_${parts[4]}_${parts[5]}`.replace(/_[a-f0-9-]+$/, '');
    }
    return parts[3];
  }

  return null;
}

// =============================================================================
// GCS URL CONVERSION
// =============================================================================

/**
 * Convert GCS URI to public URL
 * Automatically uses the correct bucket based on the URI or current source
 */
export function getVideoUrl(gcsUri) {
  if (!gcsUri) return null;

  // Extract bucket from URI if present
  const match = gcsUri.match(/gs:\/\/([^/]+)\/(.*)/);
  if (match) {
    const [, bucket, path] = match;
    return `https://storage.googleapis.com/${bucket}/${path}`;
  }

  // Fallback to current source's bucket
  const source = getIntelSource();
  const bucket = source.bucketName || DEFAULT_GCS_BUCKET;
  const path = gcsUri.replace(/gs:\/\/[^/]+\//, '');
  return `https://storage.googleapis.com/${bucket}/${path}`;
}

/**
 * Get video thumbnail URL (uses video frame extraction)
 */
export function getVideoThumbnailUrl(gcsUri) {
  // For now, thumbnails are generated from video on client side
  return null;
}

// =============================================================================
// DATA FETCHING
// =============================================================================

/**
 * Load wire summary data
 * @param {string} sourceId - Optional source ID override
 */
export async function loadWireSummary(sourceId = null) {
  const source = sourceId ? INTEL_SOURCES[sourceId] : getIntelSource();
  const path = `${INTEL_BASE_PATH}/${source.path}summary.json`;

  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to load wire summary from ${path}`);
  }
  const rawData = await response.json();
  return transformSummaryData(rawData, source);
}

/**
 * Transform exported summary to dashboard format
 * @param {Object} raw - Raw summary data
 * @param {Object} source - Intel source configuration
 */
function transformSummaryData(raw, source = null) {
  const rawByPitcher = raw.byPitcher || {};
  const byPitcher = {};

  for (const [pitcher, data] of Object.entries(rawByPitcher)) {
    byPitcher[pitcher] = {
      pitcher,
      totalPitches: data.totalPitches || 0,
      tippingRate: data.tippingRate || 0,
      avgConfidence: data.avgConfidence || data.tippingRate || 0,
      criticalTellCount: data.criticalTellCount || data.criticalTellsCount || data.criticalTells || 0,
      // Opponent intel specific fields
      threatLevel: data.threatLevel || getThreatLevelFromRate(data.tippingRate),
      headline: data.headline || '',
      mostExploitablePitch: data.mostExploitablePitch || '',
    };
  }

  // Handle pitchers array from summary - could be objects or strings
  let pitchersList = raw.pitchers || [];
  if (pitchersList.length > 0 && typeof pitchersList[0] === 'object') {
    // Extract pitcher names from objects
    pitchersList = pitchersList.map(p => p.name || p.pitcher || 'Unknown');
  }

  return {
    batchId: raw.batchId,
    generatedAt: raw.generatedAt || raw.lastUpdated,
    totalVideos: raw.totalVideos || raw.totalVideosAnalyzed || 0,
    processedSuccessfully: raw.totalVideos || raw.totalVideosAnalyzed || 0,
    failedCount: 0,
    overallTippingRate: raw.overallTippingRate || 0,
    pitchers: pitchersList,
    byPitcher,
    // Preserve report metadata
    analysisType: raw.analysisType || 'standard',
    reportTitle: raw.reportTitle || 'ADVANCED SCOUTING ANALYSIS',
    // Source info
    source: source || getIntelSource(),
    team: raw.team || source?.name || 'Unknown',
    teamId: raw.teamId || source?.id || 'unknown',
  };
}

/**
 * Get threat level from tipping rate
 */
function getThreatLevelFromRate(rate) {
  if (rate >= 0.70) return 'CRITICAL';
  if (rate >= 0.50) return 'HIGH';
  if (rate >= 0.30) return 'MEDIUM';
  return 'LOW';
}

/**
 * Load pitcher detail data
 * @param {string} pitcher - Pitcher name
 * @param {string} sourceId - Optional source ID override
 */
export async function loadPitcherDetail(pitcher, sourceId = null) {
  const source = sourceId ? INTEL_SOURCES[sourceId] : getIntelSource();

  // Build filename - handle special cases for different sources
  let filename = pitcher.toLowerCase().replace(/ /g, '-');
  if (source.id === 'wake-forest') {
    // Wake Forest uses 'wake-forest-pitcher.json'
    filename = 'wake-forest-pitcher';
  }

  const path = `${INTEL_BASE_PATH}/${source.path}${filename}.json`;
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to load ${pitcher} data from ${path}`);
  }
  const rawData = await response.json();

  // Check if this is opponent intel format (new hitter-focused data)
  if (rawData.analysisType === 'opponent_intel') {
    return transformOpponentIntelData(rawData);
  }

  // Check if this is comparative analysis data
  if (rawData.analysisType === 'comparative' || rawData.allTells) {
    return transformComparativeToPitcherData(rawData);
  }

  return transformPitcherData(rawData);
}

/**
 * Transform opponent intel format to PitcherDetailData
 * This handles the new hitter-focused analysis schema
 */
function transformOpponentIntelData(raw) {
  const pitcherName = normalizePitcherName(raw.pitcher || 'Unknown');
  const pitcherInfo = getPitcherInfo(pitcherName);

  // Transform pitchScouting to byPitchType format
  const byPitchType = {};
  const pitchScouting = raw.pitchScouting || {};

  for (const [pitchType, stats] of Object.entries(pitchScouting)) {
    // Get the most common indicators from lookFor array
    const commonIndicators = (stats.lookFor || [])
      .slice(0, 3)
      .map(lf => lf.categoryLabel || lf.category);

    byPitchType[pitchType] = {
      pitchType,
      pitchTypeLabel: PITCH_TYPE_LABELS[pitchType] || pitchType,
      totalCount: stats.totalSeen || 0,
      tippingDetectedCount: stats.tippedCount || 0,
      tippingRate: stats.tippingRate || 0,
      avgConfidence: stats.tippingRate || 0, // Use tipping rate as proxy
      commonIndicators,
      exploitabilityRank: stats.exploitabilityRank || 99,
    };
  }

  // Transform highValueTells to criticalTells format
  const criticalTells = (raw.highValueTells || []).map(t => ({
    tell: t.tell || t.whatToLookFor || '',
    pitchTypesAffected: [t.pitchRevealed].filter(Boolean),
    description: t.whatToLookFor || '',
    confidence: t.confidence || 0.8,
    occurrences: 1,
    category: t.category,
    categoryLabel: t.categoryLabel,
    timestamp: t.timestamp,
    videoUid: t.videoUid,
  }));

  // Also include exploitationMatrix as tells if we have few highValueTells
  if (criticalTells.length < 5 && raw.exploitationMatrix) {
    raw.exploitationMatrix.forEach(em => {
      if (!criticalTells.find(ct => ct.category === em.category && ct.pitchTypesAffected.includes(em.pitchRevealed))) {
        criticalTells.push({
          tell: em.description || `${em.categoryLabel} tells ${em.pitchRevealed}`,
          pitchTypesAffected: [em.pitchRevealed],
          description: em.description,
          confidence: em.frequency || 0.5,
          occurrences: em.sampleSize || 1,
          category: em.category,
          categoryLabel: em.categoryLabel,
          exploitability: em.exploitability,
          distinctiveness: em.distinctiveness,
        });
      }
    });
  }

  // Generate recommendations from atBatStrategy
  const recommendations = [];
  const atBatStrategy = raw.atBatStrategy || {};
  if (atBatStrategy.firstPitch) recommendations.push(`FIRST PITCH: ${atBatStrategy.firstPitch}`);
  if (atBatStrategy.aheadInCount) recommendations.push(`AHEAD IN COUNT: ${atBatStrategy.aheadInCount}`);
  if (atBatStrategy.behindInCount) recommendations.push(`BEHIND IN COUNT: ${atBatStrategy.behindInCount}`);
  if (atBatStrategy.twoStrikes) recommendations.push(`TWO STRIKES: ${atBatStrategy.twoStrikes}`);

  // Build map of videoUid -> tells from highValueTells for enrichment
  const tellsByVideoUid = {};
  for (const tell of (raw.highValueTells || [])) {
    if (tell.videoUid) {
      if (!tellsByVideoUid[tell.videoUid]) {
        tellsByVideoUid[tell.videoUid] = [];
      }
      tellsByVideoUid[tell.videoUid].push({
        tell: tell.tell,
        category: tell.category,
        categoryLabel: tell.categoryLabel,
        confidence: tell.confidence || 0.8,
        description: tell.whatToLookFor,
        timestamp: tell.timestamp,
        pitchRevealed: tell.pitchRevealed,
        videoUid: tell.videoUid,
      });
    }
  }

  // Build pitch type -> common tells lookup from pitchScouting
  const pitchTypeTells = {};
  const rawPitchScouting = raw.pitchScouting || {};
  for (const [pitchType, stats] of Object.entries(rawPitchScouting)) {
    const lookFor = stats.lookFor || [];
    if (lookFor.length > 0) {
      // Take the most common tell category for this pitch type
      const topTell = lookFor[0];
      pitchTypeTells[pitchType] = {
        category: topTell.category,
        categoryLabel: topTell.categoryLabel,
        frequency: topTell.frequency,
      };
    }
  }

  // Get primary tell for reference (used elsewhere, not for fallbacks)
  const primaryTell = raw.primaryTell || {};

  // Transform analyses, enriching with tells from highValueTells
  const analyses = (raw.analyses || []).map(a => {
    // Get matching tells for this video
    const matchingTells = tellsByVideoUid[a.videoUid] || [];

    // Use matching tells if available, otherwise generate from pitch type data
    let enrichedIndicators = matchingTells;

    // No fallback - only use real data from highValueTells
    // If no matching tells exist, leave indicators empty (no placeholders)

    // Get situation from data or parse from filename
    const filename = a.gcsUri?.split('/').pop() || '';
    const situation = a.situation || parseSituationFromFilename(filename) || 'bases_empty';

    return {
      videoUid: a.videoUid,
      gcsUri: a.gcsUri,
      filename,
      pitcher: pitcherName,
      gameDate: a.gameDate,
      pitchType: a.pitchTypeGroundTruth || a.pitchType,
      situation,
      setPosition: getSetPositionFromSituation(situation), // Windup or Stretch
      tippingDetected: a.tippingDetected,
      confidence: a.confidence,
      confidenceLevel: a.confidenceLevel || getConfidenceLevelFromScore(a.confidence),
      indicators: enrichedIndicators,
      recommendation: a.recommendation || '',
      processingTimeMs: a.processingTimeMs || 0,
      // Additional fields from analysis for chat context
      hitterAdvantage: a.hitterAdvantage,
      analysisNotes: a.analysisNotes,
    };
  });

  // Get threat assessment stats
  const threat = raw.threatAssessment || {};

  return {
    pitcher: pitcherName,
    fullName: raw.fullName || pitcherInfo?.name || pitcherName,
    team: raw.team || pitcherInfo?.team || 'Unknown',
    position: raw.position || (pitcherInfo?.throws === 'L' ? 'LHP' : 'RHP'),
    arsenal: raw.arsenal || pitcherInfo?.arsenal?.join('/') || '',
    notes: raw.notes || pitcherInfo?.notes || '',
    totalPitches: threat.totalAnalyzed || analyses.length,
    tippingDetectedCount: threat.tippingDetected || analyses.filter(a => a.tippingDetected).length,
    tippingRate: threat.tippingRate || 0,
    avgConfidence: threat.tippingRate || 0,
    byPitchType,
    criticalTells,
    recommendations: recommendations.length > 0 ? recommendations : ['No specific tells identified.'],
    lastUpdated: raw.generatedAt || new Date().toISOString(),
    analyses,
    // Preserve opponent intel specific data for hitter-focused views
    opponentIntel: {
      threatAssessment: threat,
      primaryTell: raw.primaryTell,
      secondaryTells: raw.secondaryTells || [],
      atBatStrategy: raw.atBatStrategy,
      situationalKeys: raw.situationalKeys,
      exploitationMatrix: raw.exploitationMatrix || [],
      highValueTells: raw.highValueTells || [],
    },
  };
}

/**
 * Get confidence level from numeric score
 */
function getConfidenceLevelFromScore(confidence) {
  if (confidence >= 0.85) return 'CRITICAL';
  if (confidence >= 0.70) return 'HIGH';
  if (confidence >= 0.50) return 'MEDIUM';
  return 'LOW';
}

/**
 * Transform comparative analysis format to PitcherDetailData
 */
function transformComparativeToPitcherData(raw) {
  const pitcherName = normalizePitcherName(raw.pitcher || 'Unknown');
  const pitcherInfo = getPitcherInfo(pitcherName);

  const byPitchType = {};
  const rawByPitchType = raw.byPitchType || {};

  for (const [pitchType, stats] of Object.entries(rawByPitchType)) {
    const tells = stats.tells || [];
    byPitchType[pitchType] = {
      pitchType,
      pitchTypeLabel: PITCH_TYPE_LABELS[pitchType] || pitchType,
      totalCount: stats.count || 0,
      tippingDetectedCount: stats.tippingCount || Math.round((stats.count || 0) * (stats.tippingRate || 0)),
      tippingRate: stats.tippingRate || 0,
      avgConfidence: stats.avgConfidence || 0,
      commonIndicators: tells.map(t => t.category || t),
    };
  }

  const analyses = (raw.analyses || []).map(a => {
    // Get situation from data or parse from filename
    const filename = a.gcsUri?.split('/').pop() || '';
    const situation = a.situation || parseSituationFromFilename(filename) || 'bases_empty';

    return {
      videoUid: a.videoUid,
      gcsUri: a.gcsUri,
      filename,
      pitcher: pitcherName,
      gameDate: a.gameDate,
      pitchType: a.pitchTypeGroundTruth || a.pitchType,
      situation,
      setPosition: getSetPositionFromSituation(situation), // Windup or Stretch
      tippingDetected: a.tippingDetected,
      confidence: a.confidence,
      confidenceLevel: a.confidenceLevel || 'LOW',
      indicators: a.indicators || [],
      recommendation: a.recommendation || '',
      processingTimeMs: a.processingTimeMs || 0,
    };
  });

  // Convert comparative criticalTells
  const criticalTells = (raw.criticalTells || []).map(t => ({
    tell: t.type || t.tell || '',
    pitchTypesAffected: t.pitchTypesAffected || [],
    description: t.description || '',
    confidence: t.confidence || t.frequency || 0,
    occurrences: t.occurrences || 1,
    frequency_in_others: t.frequency_in_others,
    distinctiveness: t.distinctiveness,
    exploitability: t.exploitability,
  }));

  // Convert recommendations
  const recommendations = (raw.recommendations || []).map(r =>
    typeof r === 'string' ? r : `[P${r.priority}] ${r.text} (${r.expected_impact})`
  );

  return {
    pitcher: pitcherName,
    fullName: raw.fullName || pitcherInfo?.name || pitcherName,
    team: raw.team || pitcherInfo?.team || 'Unknown',
    position: raw.position || (pitcherInfo?.throws === 'L' ? 'LHP' : 'RHP'),
    arsenal: raw.arsenal || pitcherInfo?.arsenal?.join('/') || '',
    notes: raw.notes || pitcherInfo?.notes || '',
    totalPitches: raw.totalCount || raw.totalPitches || analyses.length,
    tippingDetectedCount: analyses.filter(a => a.tippingDetected).length,
    tippingRate: raw.tippingRate || 0,
    avgConfidence: raw.avgConfidence || 0,
    byPitchType,
    criticalTells,
    recommendations,
    lastUpdated: raw.generatedAt || new Date().toISOString(),
    analyses,
    comparativeAnalysis: raw.allTells ? raw : null,
  };
}

/**
 * Transform exported pipeline data to dashboard format
 */
function transformPitcherData(raw) {
  const pitcherName = normalizePitcherName(raw.pitcher || 'Unknown');
  const pitcherInfo = getPitcherInfo(pitcherName);

  const byPitchType = {};
  const rawByPitchType = raw.byPitchType || {};

  for (const [pitchType, stats] of Object.entries(rawByPitchType)) {
    byPitchType[pitchType] = {
      pitchType,
      pitchTypeLabel: PITCH_TYPE_LABELS[pitchType] || pitchType,
      totalCount: stats.totalCount || stats.count || 0,
      tippingDetectedCount: stats.tippingDetectedCount || stats.tippingCount || 0,
      tippingRate: stats.tippingRate || 0,
      avgConfidence: stats.avgConfidence || 0,
      commonIndicators: stats.commonIndicators || stats.tells || [],
    };
  }

  const analyses = (raw.analyses || []).map(a => {
    // Get situation from data or parse from filename
    const filename = a.gcsUri?.split('/').pop() || '';
    const situation = a.situation || parseSituationFromFilename(filename) || 'bases_empty';

    return {
      videoUid: a.videoUid,
      gcsUri: a.gcsUri,
      filename,
      pitcher: pitcherName,
      gameDate: a.gameDate,
      pitchType: a.pitchType,
      situation,
      setPosition: getSetPositionFromSituation(situation), // Windup or Stretch
      tippingDetected: a.tippingDetected,
      confidence: a.confidence,
      confidenceLevel: a.confidenceLevel,
      indicators: a.indicators || [],
      recommendation: a.recommendation || '',
      processingTimeMs: a.processingTimeMs || 0,
    };
  });

  const criticalTells = (raw.criticalTells || []).map(t => ({
    tell: t.tell,
    pitchTypesAffected: t.pitchTypesAffected || [],
    description: t.description,
    confidence: t.confidence,
    occurrences: t.occurrences || 1,
  }));

  return {
    pitcher: pitcherName,
    fullName: raw.fullName || pitcherInfo?.name || pitcherName,
    team: raw.team || pitcherInfo?.team || 'Unknown',
    position: raw.position || (pitcherInfo?.throws === 'L' ? 'LHP' : 'RHP'),
    arsenal: raw.arsenal || pitcherInfo?.arsenal?.join('/') || '',
    notes: raw.notes || pitcherInfo?.notes || '',
    totalPitches: raw.totalPitches || analyses.length,
    tippingDetectedCount: analyses.filter(a => a.tippingDetected).length,
    tippingRate: raw.tippingRate || 0,
    avgConfidence: raw.avgConfidence || 0,
    byPitchType,
    criticalTells,
    recommendations: generateRecommendationsFromTells(criticalTells),
    lastUpdated: raw.generatedAt,
    analyses,
  };
}

/**
 * Generate recommendations from tells
 */
function generateRecommendationsFromTells(tells) {
  if (tells.length === 0) return ['No specific tells identified.'];

  return tells.slice(0, 3).map(tell => {
    const pitches = tell.pitchTypesAffected.map(p => PITCH_TYPE_LABELS[p] || p).join('/');
    return `Watch for ${tell.tell.toLowerCase()} - signals ${pitches}`;
  });
}

// =============================================================================
// HITTER SCOUTING REPORT GENERATION
// =============================================================================

/**
 * Generate hitter scouting report from pitcher detail
 */
export function generateHitterScoutingReport(pitcherDetail) {
  const pitcherInfo = getPitcherInfo(pitcherDetail.pitcher);

  if (!pitcherInfo) {
    console.warn(`Unknown pitcher: ${pitcherDetail.pitcher}. Cannot generate hitter report.`);
    return null;
  }

  // Use rich opponent intel data if available
  if (pitcherDetail.opponentIntel) {
    return generateOpponentIntelReport(pitcherDetail, pitcherInfo);
  }

  // Fallback to legacy report generation
  const sortedTells = [...pitcherDetail.criticalTells].sort((a, b) => b.confidence - a.confidence);
  const primaryTell = sortedTells[0]?.tell || 'No clear tell identified';

  const instructions = sortedTells.slice(0, 4).map(tell => ({
    situation: getSituationForTell(tell),
    lookFor: tell.tell,
    expect: tell.pitchTypesAffected.map(p => PITCH_TYPE_LABELS[p] || p).join(', '),
    action: getActionForTell(tell),
    reliability: tell.confidence,
  }));

  const arsenal = (pitcherInfo.arsenal || []).map(pitch => {
    const pitchData = pitcherDetail.byPitchType[pitch];
    return {
      pitch,
      velocity: getVelocityRange(pitcherDetail.pitcher, pitch),
      tippingRisk: getTippingRiskLevelFromRate(pitchData?.tippingRate || 0),
    };
  });

  return {
    pitcher: pitcherDetail.pitcher,
    throws: pitcherInfo.throws,
    summary: generateScoutingSummary(pitcherDetail),
    primaryTell,
    instructions,
    arsenal,
  };
}

/**
 * Generate rich hitter report from opponent intel data
 */
function generateOpponentIntelReport(pitcherDetail, pitcherInfo) {
  const intel = pitcherDetail.opponentIntel;
  const threat = intel.threatAssessment || {};
  const primary = intel.primaryTell || {};
  const secondary = intel.secondaryTells || [];
  const strategy = intel.atBatStrategy || {};
  const situational = intel.situationalKeys || {};

  // Build instructions from primary and secondary tells
  const instructions = [];

  // Primary tell instruction
  if (primary.whatToLookFor) {
    instructions.push({
      situation: primary.whenItHappens || 'Any count',
      lookFor: primary.whatToLookFor,
      expect: PITCH_TYPE_LABELS[primary.pitchRevealed] || primary.pitchRevealed,
      action: 'This is the PRIMARY tell - gear up when you see it',
      reliability: primary.confidence === 'Very High' ? 0.95 : 0.85,
    });
  }

  // Secondary tell instructions
  secondary.slice(0, 3).forEach(tell => {
    instructions.push({
      situation: 'Pre-pitch / At set',
      lookFor: tell.whatToLookFor,
      expect: PITCH_TYPE_LABELS[tell.pitchRevealed] || tell.pitchRevealed,
      action: `Adjust timing for ${tell.pitchRevealed}`,
      reliability: 0.8,
    });
  });

  // Build arsenal from byPitchType
  const arsenal = Object.entries(pitcherDetail.byPitchType || {})
    .sort((a, b) => (a[1].exploitabilityRank || 99) - (b[1].exploitabilityRank || 99))
    .map(([pitch, data]) => ({
      pitch,
      velocity: getVelocityRange(pitcherDetail.pitcher, pitch),
      tippingRisk: getTippingRiskLevelFromRate(data.tippingRate || 0),
      exploitabilityRank: data.exploitabilityRank,
    }));

  return {
    pitcher: pitcherDetail.pitcher,
    throws: pitcherInfo.throws,
    summary: threat.quickRead || generateScoutingSummary(pitcherDetail),
    primaryTell: primary.whatToLookFor || 'No clear tell identified',
    headline: threat.headline || '',
    threatLevel: threat.level || 'MEDIUM',
    instructions,
    arsenal,
    // Rich opponent intel specific data
    atBatStrategy: strategy,
    situationalKeys: situational,
    exploitationRate: threat.tippingRate || pitcherDetail.tippingRate,
    mostExploitablePitch: threat.mostExploitablePitch,
  };
}

function getSituationForTell(tell) {
  const situations = [
    'Any count',
    '2-strike counts',
    'First pitch',
    'Hitter\'s count',
    'With runners on',
  ];
  return situations[Math.floor(Math.random() * situations.length)];
}

function getActionForTell(tell) {
  const isBreakingBall = tell.pitchTypesAffected.some(p =>
    ['CB', 'SL', 'CT', 'KC'].includes(p)
  );

  if (isBreakingBall) {
    return 'Sit breaking ball, protect fastball';
  }
  if (tell.pitchTypesAffected.includes('CH')) {
    return 'Look for changeup, adjust timing';
  }
  return 'Be ready for off-speed';
}

function getVelocityRange(pitcher, pitch) {
  const velocities = {
    Skubal: { FB: '94-97', SL: '86-89', CH: '85-88', CB: '79-82' },
    Flaherty: { FB: '93-96', SL: '85-88', CB: '80-83', CT: '88-91' },
    Mize: { FB: '92-95', SI: '90-93', SL: '84-87', CH: '83-86' },
  };
  const normalizedPitcher = normalizePitcherName(pitcher);
  return velocities[normalizedPitcher]?.[pitch] || '88-92';
}

function getTippingRiskLevelFromRate(tippingRate) {
  if (tippingRate >= 0.35) return 'CRITICAL';
  if (tippingRate >= 0.25) return 'HIGH';
  if (tippingRate >= 0.15) return 'MEDIUM';
  if (tippingRate > 0) return 'LOW';
  return 'NONE';
}

function generateScoutingSummary(pitcherDetail) {
  const rate = (pitcherDetail.tippingRate * 100).toFixed(0);
  const topTells = pitcherDetail.criticalTells.slice(0, 2).map(t => t.tell).join(' and ');

  return `${pitcherDetail.pitcher} shows exploitable tells on ${rate}% of pitches. ` +
    `Primary indicators: ${topTells || 'none detected'}. ` +
    `Best approach: ${pitcherDetail.recommendations[0] || 'Work counts, look for patterns.'}`;
}

// =============================================================================
// TELL GROUPING UTILITIES
// =============================================================================

/**
 * Group video analyses by tell category for carousel display.
 * Returns videos grouped by the tell type they demonstrate.
 *
 * @param {Array} analyses - Array of VideoAnalysis objects
 * @returns {Object} { [tellCategory]: { tell, description, videos[], avgConfidence, pitchTypesAffected, count } }
 */
export function groupVideosByTell(analyses) {
  if (!analyses || analyses.length === 0) return {};

  const grouped = {};

  analyses.forEach(analysis => {
    if (!analysis.tippingDetected || !analysis.indicators) return;

    analysis.indicators.forEach(indicator => {
      const category = indicator.category || indicator.categoryLabel || indicator.tell || 'Unknown';
      const normalizedCategory = normalizeCategory(category);

      if (!grouped[normalizedCategory]) {
        grouped[normalizedCategory] = {
          tell: indicator.tell || category,
          category: normalizedCategory,
          categoryLabel: indicator.categoryLabel || category,
          description: indicator.description || '',
          videos: [],
          confidenceSum: 0,
          pitchTypesAffected: new Set(),
          count: 0,
        };
      }

      // Add video to this tell group (avoid duplicates)
      const existingVideo = grouped[normalizedCategory].videos.find(
        v => v.videoUid === analysis.videoUid
      );

      if (!existingVideo) {
        grouped[normalizedCategory].videos.push({
          ...analysis,
          tellTimestamp: indicator.timestamp,
          tellConfidence: indicator.confidence,
          tellDescription: indicator.description || indicator.whatToLookFor,
        });
      }

      grouped[normalizedCategory].confidenceSum += indicator.confidence || 0;
      grouped[normalizedCategory].count += 1;

      // Track affected pitch types (handle multiple field names)
      const affected = indicator.pitchTypesAffected
        || indicator.pitchTypesRevealed
        || [indicator.pitchRevealed].filter(Boolean);
      affected.forEach(pt => grouped[normalizedCategory].pitchTypesAffected.add(pt));
    });
  });

  // Finalize: compute averages and convert Sets to arrays
  const result = {};
  Object.entries(grouped)
    .sort((a, b) => b[1].videos.length - a[1].videos.length) // Sort by video count
    .forEach(([category, data]) => {
      result[category] = {
        tell: data.tell,
        category: data.category,
        categoryLabel: data.categoryLabel,
        description: data.description,
        videos: data.videos.sort((a, b) => (b.tellConfidence || 0) - (a.tellConfidence || 0)),
        avgConfidence: data.count > 0 ? data.confidenceSum / data.count : 0,
        pitchTypesAffected: Array.from(data.pitchTypesAffected),
        videoCount: data.videos.length,
        totalOccurrences: data.count,
      };
    });

  return result;
}

/**
 * Normalize tell category names for consistent grouping.
 */
function normalizeCategory(category) {
  if (!category) return 'Unknown';

  const normalized = category.toLowerCase().trim();

  // Map variations to canonical names
  const categoryMap = {
    'glove_position': 'Glove Position',
    'glove position': 'Glove Position',
    'glove_height': 'Glove Position',
    'arm_slot': 'Arm Slot',
    'arm slot': 'Arm Slot',
    'arm_angle': 'Arm Slot',
    'tempo': 'Tempo/Rhythm',
    'rhythm': 'Tempo/Rhythm',
    'tempo_change': 'Tempo/Rhythm',
    'grip': 'Grip Visibility',
    'grip_visibility': 'Grip Visibility',
    'finger_pressure': 'Grip Visibility',
    'leg_lift': 'Leg Lift',
    'leg lift': 'Leg Lift',
    'leg_kick': 'Leg Lift',
    'head_position': 'Head Position',
    'head position': 'Head Position',
    'head_tilt': 'Head Position',
    'set_position': 'Set Position',
    'set position': 'Set Position',
    'shoulder': 'Shoulder Alignment',
    'shoulder_alignment': 'Shoulder Alignment',
  };

  return categoryMap[normalized] || category;
}

/**
 * Get all unique tell categories from analyses.
 * @param {Array} analyses - Array of VideoAnalysis objects
 * @returns {Array} Sorted array of unique tell category names
 */
export function getUniqueTellCategories(analyses) {
  const categories = new Set();

  analyses.forEach(analysis => {
    if (analysis.indicators) {
      analysis.indicators.forEach(indicator => {
        const category = normalizeCategory(
          indicator.category || indicator.categoryLabel || indicator.tell
        );
        if (category && category !== 'Unknown') {
          categories.add(category);
        }
      });
    }
  });

  return Array.from(categories).sort();
}

/**
 * Get summary statistics for tell groupings.
 * @param {Object} groupedTells - Output from groupVideosByTell
 * @returns {Object} { totalTellTypes, totalVideosWithTells, mostCommonTell, highestConfidenceTell }
 */
export function getTellGroupSummary(groupedTells) {
  const categories = Object.values(groupedTells);

  if (categories.length === 0) {
    return {
      totalTellTypes: 0,
      totalVideosWithTells: 0,
      mostCommonTell: null,
      highestConfidenceTell: null,
    };
  }

  // Find unique videos (a video might appear in multiple tell groups)
  const uniqueVideoIds = new Set();
  categories.forEach(cat => {
    cat.videos.forEach(v => uniqueVideoIds.add(v.videoUid));
  });

  // Find most common tell (most videos)
  const mostCommon = categories.reduce((prev, curr) =>
    curr.videoCount > (prev?.videoCount || 0) ? curr : prev,
    null
  );

  // Find highest confidence tell
  const highestConfidence = categories.reduce((prev, curr) =>
    curr.avgConfidence > (prev?.avgConfidence || 0) ? curr : prev,
    null
  );

  return {
    totalTellTypes: categories.length,
    totalVideosWithTells: uniqueVideoIds.size,
    mostCommonTell: mostCommon ? mostCommon.category : null,
    highestConfidenceTell: highestConfidence ? highestConfidence.category : null,
  };
}
